/**
 * @file     MapStaticPlaceholder.tsx
 * @readme   ./CLAUDE.md
 * @purpose  Mapbox Static image of the default map view, shown while GL boots.
 *           `low` covers the same area at half the pixels (one zoom level out)
 *           and is what the server shell paints, so the map pane is a real
 *           map in the first frame; `full` is the sharper image the live map
 *           view layers on top. Both fill their positioned parent.
 */
import { INITIAL_VIEW } from './mapboxMapConstants';

type Props = {
  /** 'auto' (server shell): the browser picks by prefers-color-scheme, since
   *  the server does not know the visitor's theme. */
  mode: 'light' | 'dark' | 'auto';
  quality: 'low' | 'full';
  /** fetchpriority=high for the copy that is the first paint's largest element. */
  priority?: boolean;
};

// Phones get a square, wider panes a 16:10 frame (the pane is 55% of a
// landscape viewport). One zoom level out at half the pixels shows the same
// area, so the full image lands exactly over the low one.
const FRAMES = {
  full: { zoom: INITIAL_VIEW.zoom, phone: '640x640', wide: '1280x800' },
  low: { zoom: INITIAL_VIEW.zoom - 1, phone: '320x320', wide: '640x400' },
} as const;

/** Phone and wide-pane URLs for one theme and quality, or null without a token. */
export function mapStaticUrls(mode: 'light' | 'dark', quality: Props['quality']): { phone: string; wide: string } | null {
  const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
  if (!token) return null;
  const frame = FRAMES[quality];
  const base = `https://api.mapbox.com/styles/v1/mapbox/${mode === 'dark' ? 'dark-v11' : 'light-v11'}/static/${INITIAL_VIEW.longitude},${INITIAL_VIEW.latitude},${frame.zoom}`;
  const qs = `?access_token=${token}`;
  return { phone: `${base}/${frame.phone}${qs}`, wide: `${base}/${frame.wide}${qs}` };
}

export function MapStaticPlaceholder({ mode, quality, priority = false }: Props) {
  const urls = mapStaticUrls(mode === 'dark' ? 'dark' : 'light', quality);
  const dark = mode === 'auto' ? mapStaticUrls('dark', quality) : null;
  if (!urls) return null;
  return (
    <picture>
      {/* An <img> in the server HTML is found by the preload scanner, so it
          paints before hydration (a CSS background was fetched too late to
          ever be the LCP). One source per theme and frame: only one loads. */}
      {dark && <source media="(prefers-color-scheme: dark) and (max-width: 900px)" srcSet={dark.phone} />}
      {dark && <source media="(prefers-color-scheme: dark)" srcSet={dark.wide} />}
      <source media="(max-width: 900px)" srcSet={urls.phone} />
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={urls.wide}
        alt=""
        aria-hidden
        fetchPriority={priority ? 'high' : 'auto'}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // Soft blur: reads as the map warming up rather than a pixelated still.
          filter: 'blur(2px) saturate(1.05)',
          transform: 'scale(1.01)',
        }}
      />
    </picture>
  );
}
